import React from 'react';
import { T } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from '../customer/CustomersUI';
import { COPY } from './vendors.copy';

/**
 * Adding a supplier, and editing one.
 *
 * One dialog for both, so a field added later cannot be forgotten on the
 * other form.
 *
 * Only the four things a shop actually knows about a supplier: who they are,
 * the number somebody rings, the person who answers it, and the city. The
 * vendor schema carries a good deal more — it shares the same contact shape as
 * a customer, so it has a birthday and a passport number on it — and none of
 * that belongs on this form.
 *
 * The balance is not editable and never will be here. It is the sum of the
 * ledger, and the way to change it is to add a line to the ledger.
 */
export default function VendorFormDialog({ vendor, onClose, onSubmit }) {
  const editing = Boolean(vendor);

  const [name, setName] = React.useState(() => (vendor && vendor.name) || '');
  const [phone, setPhone] = React.useState(() => (vendor && vendor.phone) || '');
  const [agent, setAgent] = React.useState(() => (vendor && vendor.agent) || '');
  const [city, setCity] = React.useState(() => (vendor && vendor.city) || '');

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async () => {
    if (busy) return;
    if (!name.trim()) return setError(COPY.nameRequired);

    setBusy(true);
    setError('');

    try {
      await onSubmit({
        name: name.trim(),
        phone: phone.trim(),
        agent: agent.trim(),
        city: city.trim(),
      });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || COPY.saveFailed);
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy} width={430}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={editing ? COPY.formEdit : COPY.formAdd}
          onClose={onClose}
          locked={busy}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 18 }}>
          <PillField
            name="vendor-name"
            value={name}
            onChange={(value) => {
              setName(value);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.name}
            disabled={busy}
            autoFocus
          />

          <PillField
            name="vendor-phone"
            value={phone}
            onChange={setPhone}
            onEnter={submit}
            placeholder={COPY.phone}
            disabled={busy}
          />

          <PillField
            name="vendor-agent"
            value={agent}
            onChange={setAgent}
            onEnter={submit}
            placeholder={COPY.agent}
            disabled={busy}
          />

          <PillField
            name="vendor-city"
            value={city}
            onChange={setCity}
            onEnter={submit}
            placeholder="City"
            disabled={busy}
          />
        </div>

        <div style={{ fontSize: 11.5, color: T.inkFaint, lineHeight: 1.6, paddingTop: 10 }}>
          The agent is the person you deal with there.
        </div>

        <div style={{ paddingTop: 14 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          style={{ width: '100%', marginTop: error ? 12 : 18 }}
        >
          {editing ? COPY.save : COPY.formAdd}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
